import React from 'react';
import Helmet from 'react-helmet';
import { success } from '../../util/Response';
import { AuthPage } from './components';
import { SettingsView } from './SettingsView';
import {
    ForgotView,
    LoginView,
    ResetView,
    SignupView,
    VerifyView,
} from './views';

/**
 * Account pages (W1.2). Routed only when ENABLE_ACCOUNTS is on at build time
 * (router/routes.js); every path shares this screen and the route names the
 * view. The forms render on the server and talk to api-next (/v2) from the
 * browser, so there's nothing to fetch here.
 */
export type AccountView =
    'signup' | 'login' | 'verify' | 'forgot' | 'reset' | 'settings';

export const fetchData = (view: AccountView) =>
    Promise.resolve(success({ view }));

const TITLES: Record<AccountView, string> = {
    signup: 'Sign up',
    login: 'Log in',
    verify: 'Verify your email',
    forgot: 'Reset your password',
    reset: 'Set a new password',
    settings: 'Account settings',
};

const VIEWS = {
    signup: SignupView,
    login: LoginView,
    verify: VerifyView,
    forgot: ForgotView,
    reset: ResetView,
};

export const Account = ({
    data,
    location,
}: {
    data: { view: AccountView };
    location: { search: string };
}) => {
    const head = (
        <Helmet
            title={`${TITLES[data.view]} | NearBites`}
            meta={[
                // Forms, not content; reset links carry a secret in the URL.
                { name: 'robots', content: 'noindex, nofollow' },
                { name: 'referrer', content: 'no-referrer' },
            ]}
        />
    );
    if (data.view === 'settings') {
        return (
            <>
                {head}
                <SettingsView />
            </>
        );
    }
    const View = VIEWS[data.view];
    return (
        <AuthPage>
            {head}
            <View search={location?.search ?? ''} />
        </AuthPage>
    );
};
